import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  LinearProgress,
  alpha,
} from "@mui/material";
import LogTable from "../components/logging/LogTable";
import LogDialog from "../components/logging/LogDialog";
import SearchForm from "../components/logging/SearchForm";
import { fetchEmailLogs } from "../api/MailFlowLogs";
import { useAtomValue } from "jotai";
import { sessionAtom } from "../atoms/auth"; // Ensure path is correct

// Helper to get UTC ISO String (e.g., 2023-10-27T10:00:00.000Z)
function getUtcTime(minutesAgo = 0) {
  const date = new Date();
  date.setMinutes(date.getMinutes() + minutesAgo); // Add/Subtract minutes
  return date.toISOString();
}

function toUtcISOString(date) {
  return new Date(date).toISOString();
}

const MailFlowLogs = () => {
  // Although API handles token, we can subscribe to session changes here if needed
  useAtomValue(sessionAtom);

  const [logs, setLogs] = useState([]);
  const [isLoading, setLoading] = useState(true);

  // Initial state: Last 30 minutes in proper UTC format
  const [filtersData, setFiltersData] = useState({
    date_range: {
      from_date: getUtcTime(-30),
      to_date: getUtcTime(0),
    },
  });

  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    per_page: 25,
    total_pages: 1,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [sortOrder, setSortOrder] = useState({
    field: "log_timestamp",
    order: "desc",
  });

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filtersData, pagination.per_page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await fetchEmailLogs(
        pagination.page,
        pagination.per_page,
        filtersData
      );

      if (response && response.data) {
        setLogs(response.data.data);

        // FIX: Fallback to current per_page if API response doesn't return it
        // This prevents 'undefined' being set in state
        const perPage = response.data.page_size || pagination.per_page;

        setPagination({
          total_count: response.data.total_count || 0,
          page: response.data.current_page || 1,
          per_page: perPage,
          total_pages: response.data.total_pages || 1,
        });
      }
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedLog(null);
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleSortChange = (field) => {
    const isAsc = sortOrder.field === field && sortOrder.order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setSortOrder({ field, order: newOrder });

    const sortedLogs = [...logs].sort((a, b) => {
      if (a[field] < b[field]) return newOrder === "asc" ? -1 : 1;
      if (a[field] > b[field]) return newOrder === "asc" ? 1 : -1;
      return 0;
    });
    setLogs(sortedLogs);
  };

  const handleSearchChange = (searchData) => {
    const fixed = {
      ...searchData,
      date_range: {
        from_date: toUtcISOString(searchData.date_range.from_date),
        to_date: toUtcISOString(searchData.date_range.to_date),
      },
    };

    setFiltersData(fixed);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      page: 1, // Always reset to the first page when changing page size
    }));
  };

  const TableDisplay = () => {
    // If empty and NOT loading, show message (handled inside LogTable now too, but safe to keep)
    if (!isLoading && logs.length === 0) {
      return;
    }

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: 4,
          width: "100%",
          overflow: "hidden",
          // Glassmorphism Container
          backdropFilter: "blur(20px)",
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.6),
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[4],
        }}
      >
        <LogTable
          logs={logs}
          pagination={pagination}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          sortOrder={sortOrder}
          isLoading={isLoading}
        />
      </TableContainer>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        p: 2,
      }}
    >
     
      <SearchForm onSearchChange={handleSearchChange} />

      {isLoading && (
        <Box sx={{ width: "100%", mt: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <TableDisplay />

      {selectedLog && (
        <LogDialog
          open={dialogOpen}
          log={selectedLog}
          onClose={handleDialogClose}
        />
      )}
    </Box>
  );
};

export default MailFlowLogs;
